// Libs
import {
  Avatar,
  Box,
  FormHelperText,
  Grow,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import classNames from 'classnames/bind';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
// Components, Layouts, Pages
import { BaseInputSearch } from '~/components/common';
// Others
import { DEFAULT_NUMBER_ZERO, EMPTY_STRING } from '~/utils/constants/common';
import { IBaseOption } from '~/utils/interface/common';
import { TSelectLayout } from '~/utils/type/common';
import { SelectLayoutEnum } from '~/utils/enum/common';
// Styles, images, icons
import { icons } from '~/assets';
import styles from './BaseSelect.module.scss';

type Props<T> = {
  label?: string;
  name?: string;
  value?: string | string[];
  options?: T[];
  required?: boolean;
  multiple?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  errorMessage?: string;
  isAutoWidth?: boolean;
  width?: number | string;
  height?: number | string;
  totalOptions?: number;
  hasMore?: boolean;
  layout?: TSelectLayout;
  disablePortal?: boolean;
  onSearchChange?: (searchValue: string) => void;
  onChange?: (optionSelected: T) => void;
  onLoadMore?: () => void;
};

const cx = classNames.bind(styles);

const BaseSelect = <T extends IBaseOption>(props: Props<T>) => {
  //#region Destructuring Props
  const {
    label,
    name,
    value,
    options = [],
    required = false,
    multiple = false,
    disabled,
    isLoading = false,
    errorMessage,
    isAutoWidth,
    width,
    height,
    hasMore = false,
    layout = 'vertical',
    disablePortal = false,
    onSearchChange,
    onChange,
    onLoadMore,
  } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [valueSelected, setValueSelected] = useState<string | string[]>(multiple ? [] : EMPTY_STRING);
  const [isShowMenu, setIsShowMenu] = useState<boolean>(false);
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    setValueSelected(value || (multiple ? [] : EMPTY_STRING));

    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleShowMenu = () => {
    setIsShowMenu(true);
  };

  const handleCloseMenu = () => {
    setIsShowMenu(false);
  };

  const handleSelectChange = (optionSelected: T) => {
    if (multiple) {
      const newValue: string[] = Array.isArray(valueSelected)
        ? valueSelected.includes(optionSelected.value)
          ? valueSelected.filter((val) => val !== optionSelected.value)
          : [...valueSelected, optionSelected.value]
        : [optionSelected.value];
      setValueSelected(newValue);
      onChange && onChange(newValue as any);
    } else {
      setValueSelected(optionSelected.value);
      onChange && onChange(optionSelected);
      handleCloseMenu();
    }
  };

  const handleLoadMore = () => {
    onLoadMore && onLoadMore();
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
    onSearchChange && onSearchChange(value);
  };
  //#endregion Handle Function

  return (
    <Box
      component={'div'}
      id='baseSelectComponent'
      className={cx('select', { horizontal: layout === SelectLayoutEnum.HORIZONTAL })}
      style={{ width, height }}
    >
      {label && (
        <InputLabel required={required} className={cx('label')}>
          {label}
        </InputLabel>
      )}

      <Select
        id={name || 'select'}
        multiple={multiple}
        value={valueSelected || EMPTY_STRING}
        disabled={disabled}
        autoWidth={isAutoWidth}
        fullWidth
        className={cx('selectField', { disabled })}
        open={isShowMenu}
        onOpen={handleShowMenu}
        onClose={handleCloseMenu}
        IconComponent={() => <Avatar src={icons.commonIconArrowDown} className={cx('selectArrow')} />}
        MenuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          disablePortal: disablePortal,
          className: cx('menu'),
          PaperProps: {
            className: cx('paper'),
            id: 'paper',
          },
          MenuListProps: {
            className: cx('menuList'),
            autoFocusItem: false,
          },
        }}
        sx={{
          '.MuiSelect-select': {
            paddingTop: '10px',
            paddingBottom: '10px',
            paddingRight: '0 !important',
            minHeight: '0 !important',
            display: 'flex',
            alignItems: 'center',
          },
        }}
        displayEmpty
        renderValue={(selected) => {
          const selectedOption = options.find((option) => option.value === selected);

          if (isLoading) {
            return (
              <Grow in={isLoading} style={{ transformOrigin: '0 0 0' }}>
                <Stack
                  direction={'row'}
                  alignItems={'center'}
                  gap={'4px'}
                  className={cx('selectedOption', { placeholder: true })}
                >
                  <CircularProgress size={14} />
                  {t('common_loading_placeholder')}
                </Stack>
              </Grow>
            );
          }

          if (multiple && Array.isArray(selected)) {
            const selectedLabels = (selected as string[])
              .map((val) => options.find((option) => option.value === val)?.label)
              .filter(Boolean)
              .join(', ');

            return (
              <Box className={cx('selectedOption', { placeholder: selected.length === DEFAULT_NUMBER_ZERO })}>
                {selectedLabels || t('common_select_placeholder')}
              </Box>
            );
          }

          return (
            <Box className={cx('selectedOption', { placeholder: !selectedOption })}>
              {selectedOption ? selectedOption.label : t('common_select_placeholder')}
            </Box>
          );
        }}
      >
        {onSearchChange && (
          <Box component={'div'} className={cx('inputGroup')} onMouseDown={(e) => e.stopPropagation()}>
            <BaseInputSearch width={'100%'} value={searchKey} onChange={handleSearchChange} />
          </Box>
        )}

        <InfiniteScroll
          dataLength={options.length}
          next={handleLoadMore}
          hasMore={hasMore}
          loader={
            <Stack direction={'row'} alignItems={'center'} justifyContent={'center'} height={35}>
              <CircularProgress size={14} />
            </Stack>
          }
          scrollableTarget={'paper'}
          endMessage={
            options?.length === DEFAULT_NUMBER_ZERO && (
              <Stack direction={'row'} alignItems={'center'} justifyContent={'center'} height={35}>
                <Typography component={'p'} className={cx('noData')}>
                  {t('common_no_data')}
                </Typography>
              </Stack>
            )
          }
        >
          {Array.isArray(options) &&
            options?.length > DEFAULT_NUMBER_ZERO &&
            options?.map((option) => {
              const selected = multiple
                ? valueSelected?.includes(option.value)
                : valueSelected === option.value;
              return (
                <MenuItem
                  selected={selected}
                  key={option?.value}
                  value={option?.value}
                  className={cx('menuItem')}
                  onClick={() => handleSelectChange(option)}
                >
                  {option?.label || EMPTY_STRING}
                </MenuItem>
              );
            })}
        </InfiniteScroll>
      </Select>

      {errorMessage && <FormHelperText className={cx('errorMessage')}>{errorMessage}</FormHelperText>}
    </Box>
  );
};

export default BaseSelect;
